import {
  Children,
  cloneElement,
  isValidElement,
  memo,
  useEffect,
  useRef,
  useState,
} from 'react';

import Geohash from 'latlon-geohash';
import { useDeepCompareEffectForMaps } from '../../../helpers/maps/use-deep-compare';

import { MapContainer } from './styles';

interface CustoMapProps extends google.maps.MapOptions {
  onClick?: (e: google.maps.MapMouseEvent) => void;
  onIdle?: (map: google.maps.Map) => void;
  heatMapGeoHashes?: {
    geoHashs: { geoHash: string; length: number }[];
    excludedGeoHashs?: { geoHash: string; length: number }[];
  };
  children?: React.ReactNode;
}

const mainMapGradient = [
  'rgba(29,28,56,0)',
  'rgba(29,28,56,1)',
  'rgba(48,48,119,1)',
  'rgba(71,48,152,1)',
  'rgba(115,37,149,1)',
  'rgba(158,30,149,1)',
  'rgba(196,26,149,1)',
  'rgba(216,55,107,1)',
  'rgba(235,55,41,1)',
  'rgba(235,104,37,1)',
  'rgba(235,128,34,1)',
  'rgba(241,160,25,1)',
  'rgba(243,184,20,1)',
  'rgba(244,213,26,1)',
];

const excludedMapGradient = [
  'rgba(255,0,0,0)',
  'rgba(255,0,0,0.2)',
  'rgba(255,0,0,0.4)',
  'rgba(255,0,0,0.6)',
  'rgba(255,0,0,0.8)',
  'rgba(255,0,0,1)',
];

const CustomMap: React.FC<CustoMapProps> = ({
  onClick,
  onIdle,
  children,
  heatMapGeoHashes,
  ...options
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const [mainHeatMap, setMainHeatMap] =
    useState<google.maps.visualization.HeatmapLayer>();
  const [excludedHeatMap, setExcludedHeatMap] =
    useState<google.maps.visualization.HeatmapLayer>();

  useEffect(() => {
    if (ref.current && !map) {
      setMap(new window.google.maps.Map(ref.current, {}));
    }
  }, [ref, map]);

  // Inicializa os heatmaps apenas uma vez quando o mapa é criado
  useEffect(() => {
    if (map && !mainHeatMap) {
      const newMainHeatMap = new window.google.maps.visualization.HeatmapLayer({
        data: [],
        map,
        gradient: mainMapGradient,
      });
      setMainHeatMap(newMainHeatMap);
    }

    if (map && !excludedHeatMap) {
      const newExcludedHeatMap =
        new window.google.maps.visualization.HeatmapLayer({
          data: [],
          map,
          gradient: excludedMapGradient,
          opacity: 0.6,
        });
      setExcludedHeatMap(newExcludedHeatMap);
    }
  }, [map, mainHeatMap, excludedHeatMap]);

  // Atualiza os dados dos heatmaps quando os dados mudam
  useEffect(() => {
    if (!map || !mainHeatMap || !excludedHeatMap) return;

    map.getStreetView().setVisible(false);

    if (heatMapGeoHashes?.geoHashs?.length) {
      // Processa geohashes principais
      const mainPoints: any[] = [];
      heatMapGeoHashes.geoHashs.forEach(
        (item: { geoHash: string; length: number }) => {
          const { lat, lon } = Geohash.decode(item.geoHash);
          mainPoints.push({
            location: new google.maps.LatLng(lat, lon),
            weight: item?.length,
          });
        },
      );

      // Atualiza dados do heatmap principal
      mainHeatMap.setData(mainPoints);
      mainHeatMap.setMap(map);

      // Processa geohashes excluídos
      if (heatMapGeoHashes.excludedGeoHashs?.length) {
        const excludedPoints: any[] = [];
        heatMapGeoHashes.excludedGeoHashs.forEach(
          (item: { geoHash: string; length: number }) => {
            const { lat, lon } = Geohash.decode(item.geoHash);
            excludedPoints.push({
              location: new google.maps.LatLng(lat, lon),
              weight: item?.length,
            });
          },
        );

        // Atualiza dados do heatmap excluído
        excludedHeatMap.setData(excludedPoints);
        excludedHeatMap.setMap(map);
      } else {
        // Limpa explicitamente os pontos excluídos quando não há dados de exclusão
        excludedHeatMap.setData([]);
        excludedHeatMap.setMap(null);
      }
    } else {
      // Limpa ambos os heatmaps quando não há dados
      mainHeatMap.setData([]);
      excludedHeatMap.setData([]);
      mainHeatMap.setMap(null);
      excludedHeatMap.setMap(null);
    }
  }, [heatMapGeoHashes, map, mainHeatMap, excludedHeatMap]);

  // because React does not do deep comparisons, a custom hook is used
  // see discussion in https://github.com/googlemaps/js-samples/issues/946
  useDeepCompareEffectForMaps(() => {
    if (map) {
      map.setOptions(options);
      // Não recolocamos os heatmaps aqui pois eles são gerenciados no useEffect acima
    }
  }, [map, options]);

  useEffect(() => {
    if (map) {
      ['click', 'idle'].forEach(eventName =>
        google.maps.event.clearListeners(map, eventName),
      );

      if (onClick) {
        map.addListener('click', onClick);
      }

      if (onIdle) {
        map.addListener('idle', () => onIdle(map));
      }
    }
  }, [map, onClick, onIdle]);

  return (
    <>
      <MapContainer ref={ref} />
      {Children.toArray(children)
        .filter(isValidElement)
        .map(child => cloneElement(child, { map } as any))}
    </>
  );
};

export default memo(CustomMap);
